import { useEffect, useState } from 'react';
import {
  Input,
  Pagination,
  Tag,
  Typography,
} from '@arco-design/web-react';
import {
  WorkbenchOperationCard,
} from '../components/WorkbenchOperations';
import type {
  PortalOperation,
  PortalOperationList,
  PortalOperationPageResponse,
} from '../types/portal.types';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';

const { Text } = Typography;

type PortalOperationListViewProps = {
  operationLists: PortalOperationList[];
  groupFilters: PortalOperationPageResponse['groupFilters'];
  keyword: string;
  activeGroupCode?: string;
  page: number;
  pageSize: number;
  total: number;
  onSearch: (keyword: string) => void;
  onGroupChange: (groupCode?: string) => void;
  onPageChange: (page: number) => void;
  onNavigate: (path: string) => void;
};

function PortalOperationListView(
  props: PortalOperationListViewProps,
) {
  const { message } = usePortalOverlay();
  const [keywordInput, setKeywordInput] =
    useState(props.keyword);

  useEffect(() => {
    setKeywordInput(props.keyword);
  }, [props.keyword]);

  const allOperationCount = props.groupFilters.reduce(
    (total, group) => total + group.operationCount,
    0,
  );

  const openOperation = (operation: PortalOperation) => {
    if (!operation.enabled) {
      message.warning(
        operation.unavailableReason ?? '当前操作暂不可用',
      );
      return;
    }
    props.onNavigate(operation.routePath);
  };

  return (
    <div className="reference-management-page portal-operation-list-page">
      <section className="portal-operation-list-page__content">
        <div className="ui-filter-bar ui-filter-bar--compact">
          <div className="ui-filter-bar__fields">
            <Input.Search
              allowClear
              value={keywordInput}
              placeholder="搜索操作名称、说明或来源系统"
              onChange={value => {
                setKeywordInput(value);
                if (!value) props.onSearch('');
              }}
              onSearch={props.onSearch}
            />
          </div>
          <div className="ui-filter-bar__actions">
            <Text type="secondary">
              只展示当前用户可见的操作
            </Text>
          </div>
        </div>

        <nav
          className="portal-operation-list-tabs"
          aria-label="操作列表分类"
        >
          <button
            className={!props.activeGroupCode ? 'active' : ''}
            type="button"
            onClick={() => props.onGroupChange(undefined)}
          >
            全部操作<span>{allOperationCount}</span>
          </button>
          {props.groupFilters.map(group => (
            <button
              className={
                props.activeGroupCode === group.groupCode
                  ? 'active' : ''
              }
              key={group.groupCode}
              type="button"
              onClick={() =>
                props.onGroupChange(group.groupCode)}
            >
              {group.groupName}
              <span>{group.operationCount}</span>
            </button>
          ))}
        </nav>

        <div className="portal-operation-groups">
          {props.operationLists.map(list => (
            <section
              className="portal-operation-group"
              key={list.listId}
            >
              <div className="portal-operation-group__head">
                <div>
                  <strong>{list.name}</strong>
                  <span>{list.systemName}</span>
                </div>
                <Tag color="arcoblue">
                  {list.operations.length} 项
                </Tag>
              </div>
              <div className="portal-operation-grid portal-operation-grid--all">
                {list.operations.map(operation => (
                  <WorkbenchOperationCard
                    key={operation.operationId}
                    operation={operation}
                    compact
                    onOpen={openOperation}
                  />
                ))}
              </div>
            </section>
          ))}
          {props.operationLists.length === 0 ? (
            <div className="portal-operation-list-page__empty">
              <strong>没有匹配的操作</strong>
              <Text type="secondary">
                请调整搜索条件或切换操作列表。
              </Text>
            </div>
          ) : null}
        </div>

        <Pagination
          current={props.page}
          pageSize={props.pageSize}
          total={props.total}
          size="small"
          hideOnSinglePage
          onChange={props.onPageChange}
        />
      </section>
    </div>
  );
}

export default PortalOperationListView;
